import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../services/api-client';
import { useAuth } from './AuthContext';

export type MePermissions = {
  permissions: string[];
  roleSlugs: string[];
  roleNames: string[];
  isSuperAdmin: boolean;
};

type AdminRbacContextValue = {
  permissions: Set<string>;
  roleSlugs: string[];
  roleNames: string[];
  isSuperAdmin: boolean;
  /** True until the first /me/permissions fetch finishes for an admin session. */
  isLoading: boolean;
  /** True after at least one successful or failed permission fetch. */
  isReady: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  canAll: (permissions: string[]) => boolean;
};

const AdminRbacContext = createContext<AdminRbacContextValue | undefined>(undefined);

/**
 * Phase 1 fallback: admins with roles.manage or super-admin slug get full UI access
 * when catalog is empty (migration window). Bare admins without roles are denied modules.
 */
function buildAccess(data: MePermissions | null): {
  permissions: Set<string>;
  fullAccess: boolean;
} {
  if (!data) return { permissions: new Set(), fullAccess: false };
  const set = new Set(data.permissions);
  const fullAccess =
    data.isSuperAdmin ||
    set.has('roles.manage') ||
    data.roleSlugs.includes('super-administrator');
  return { permissions: set, fullAccess };
}

/** Accept camelCase or snake_case API payloads. */
function normalizeMePermissions(raw: unknown): MePermissions {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const permissions = data.permissions ?? data.permissionKeys ?? data.permission_keys;
  const roleSlugs = data.roleSlugs ?? data.role_slugs;
  const roleNames = data.roleNames ?? data.role_names;
  const isSuperAdmin = data.isSuperAdmin ?? data.is_super_admin;
  return {
    permissions: Array.isArray(permissions) ? (permissions as string[]) : [],
    roleSlugs: Array.isArray(roleSlugs) ? (roleSlugs as string[]) : [],
    roleNames: Array.isArray(roleNames) ? (roleNames as string[]) : [],
    isSuperAdmin: Boolean(isSuperAdmin),
  };
}

export const AdminRbacProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const isAdminSession = Boolean(isAuthenticated && user?.role === 'admin');

  const [raw, setRaw] = useState<MePermissions | null>(null);
  /** Start loading immediately for admin sessions so route guards wait (avoid Forbidden flash). */
  const [isLoading, setIsLoading] = useState(isAdminSession);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullAccess, setFullAccess] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || user?.role !== 'admin' || !user?.id) {
      setRaw(null);
      setFullAccess(false);
      setIsLoading(false);
      setIsReady(true);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<MePermissions | Record<string, unknown>>(
        '/api/v1/admin/rbac/me/permissions'
      );
      const data = normalizeMePermissions(res.data);
      const access = buildAccess(data);
      setRaw(data);
      setFullAccess(access.fullAccess);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
      setRaw(null);
      setFullAccess(false);
    } finally {
      setIsLoading(false);
      setIsReady(true);
    }
  }, [isAuthenticated, user?.role, user?.id]);

  useEffect(() => {
    if (isAdminSession) {
      setIsLoading(true);
      setIsReady(false);
    }
    void refresh();
  }, [refresh, isAdminSession]);

  const permissionSet = useMemo(() => new Set(raw?.permissions ?? []), [raw]);

  const can = useCallback(
    (permission: string) => {
      if (fullAccess) return true;
      return permissionSet.has(permission);
    },
    [fullAccess, permissionSet]
  );

  const canAny = useCallback(
    (permissions: string[]) => {
      if (permissions.length === 0) return true;
      if (fullAccess) return true;
      return permissions.some((p) => permissionSet.has(p));
    },
    [fullAccess, permissionSet]
  );

  const canAll = useCallback(
    (permissions: string[]) => {
      if (permissions.length === 0) return true;
      if (fullAccess) return true;
      return permissions.every((p) => permissionSet.has(p));
    },
    [fullAccess, permissionSet]
  );

  const value = useMemo<AdminRbacContextValue>(
    () => ({
      permissions: permissionSet,
      roleSlugs: raw?.roleSlugs ?? [],
      roleNames: raw?.roleNames ?? [],
      isSuperAdmin: Boolean(raw?.isSuperAdmin || fullAccess),
      isLoading,
      isReady,
      error,
      refresh,
      can,
      canAny,
      canAll,
    }),
    [permissionSet, raw, fullAccess, isLoading, isReady, error, refresh, can, canAny, canAll]
  );

  return <AdminRbacContext.Provider value={value}>{children}</AdminRbacContext.Provider>;
};

export function useAdminRbac(): AdminRbacContextValue {
  const ctx = useContext(AdminRbacContext);
  if (!ctx) {
    throw new Error('useAdminRbac must be used within AdminRbacProvider');
  }
  return ctx;
}

export function usePermission(permission: string): boolean {
  const { can, isLoading, isReady } = useAdminRbac();
  if (isLoading || !isReady) return false;
  return can(permission);
}

export function useAnyPermission(permissions: string[]): boolean {
  const { canAny, isLoading, isReady } = useAdminRbac();
  if (isLoading || !isReady) return false;
  return canAny(permissions);
}

export const Can: React.FC<{
  permission?: string;
  anyOf?: string[];
  allOf?: string[];
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ permission, anyOf, allOf, fallback = null, children }) => {
  const { can, canAny, canAll, isLoading, isReady } = useAdminRbac();
  if (isLoading || !isReady) return null;
  let allowed = true;
  if (permission) allowed = can(permission);
  if (allowed && anyOf) allowed = canAny(anyOf);
  if (allowed && allOf) allowed = canAll(allOf);
  return allowed ? <>{children}</> : <>{fallback}</>;
};
